import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { fmtDate, fmtDateTime } from "../lib/format";
import api, { fileUrl } from "../lib/api";
import { Plus, FileInput, AlertTriangle, ScanLine, CheckCircle2, Clock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { ReceivedInvoiceModal, CATEGORIES } from "../components/ReceivedInvoiceModal";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";
const EMPTY = { supplier_name: "", supplier_ico: "", supplier_dic: "", number: "", var_symbol: "", bank_account: "", issue_date: "", due_date: "", subtotal: 0, vat_rate: 21, vat: 0, total: 0, category: "material", job_id: "", notes: "", status: "neuhrazena" };
const FILTERS = [["", "Vše"], ["neuhrazena", "Neuhrazené"], ["overdue", "Po splatnosti"], ["soon", "Splatné do 5 dní"], ["uhrazena", "Uhrazené"]];

export default function ReceivedInvoices() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("neuhrazena");
    const [scanBusy, setScanBusy] = useState(false);
    const scanRef = useRef(null);

    const load = () => {
        api.get("/received-invoices").then((r) => setItems(r.data));
        api.get("/received-invoices/summary").then((r) => setSummary(r.data));
    };
    useEffect(() => { load(); api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);
    useEffect(() => {
        const openId = new URLSearchParams(window.location.search).get("open");
        if (openId && items.length && !editing) { const r = items.find((x) => x.id === openId); if (r) setEditing({ ...r, job_id: r.job_id || "" }); }
    }, [items]); // eslint-disable-line

    const scan = async (file) => {
        if (!file) return;
        setScanBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/received-invoices/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setEditing({ ...EMPTY, ...data, _file: file, _scanned: true });
            toast.success("Doklad přečten — zkontrolujte údaje a uložte");
        } catch (e) { toast.error(e.response?.data?.detail || "Načtení dokladu selhalo"); }
        finally { setScanBusy(false); }
    };

    const pay = async (r) => {
        try {
            if (r.status === "uhrazena") await api.post(`/received-invoices/${r.id}/unpay`);
            else await api.post(`/received-invoices/${r.id}/pay`);
            toast.success(r.status === "uhrazena" ? "Označeno jako neuhrazené" : "Označeno jako uhrazené");
            load();
        } catch { toast.error("Změna stavu selhala"); }
    };

    const del = async (id) => {
        if (!window.confirm("Smazat přijatou fakturu?")) return;
        await api.delete(`/received-invoices/${id}`);
        load();
    };

    const filtered = items.filter((r) => {
        if (filter === "overdue") return r.due_state === "overdue";
        if (filter === "soon") return r.due_state === "soon";
        return !filter || r.status === filter;
    });

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="received-invoices-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Závazky</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Přijaté faktury</h1>
                    <p className="text-sm text-slate-600 mt-2">Faktury od dodavatelů a stavebnin s hlídáním splatnosti — upozornění 5 dní předem.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Link to="/banka" data-testid="bank-link" className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">Spárovat platby z banky →</Link>
                    <button onClick={() => scanRef.current?.click()} disabled={scanBusy} data-testid="scan-received-button"
                            className="inline-flex items-center gap-2 border-2 border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 px-4 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <ScanLine className={`w-4 h-4 ${scanBusy ? "animate-pulse" : ""}`} /> {scanBusy ? "AI čte doklad…" : "Naskenovat / nahrát PDF (AI)"}
                    </button>
                    <input ref={scanRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="scan-received-input"
                           onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; scan(f); }} />
                    <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-received-button"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <Plus className="w-4 h-4" /> Ručně
                    </button>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="received-summary">
                    <Stat icon={Wallet} label="Neuhrazeno celkem" value={fmt(summary.unpaid_total)} sub={`${summary.unpaid_count} faktur`} testid="sum-unpaid" />
                    <Stat icon={Clock} label={`Splatné do ${summary.due_soon_days} dní`} value={fmt(summary.due_soon_total)} sub={`${summary.due_soon_count} faktur`} warn={summary.due_soon_count > 0} testid="sum-soon" />
                    <Stat icon={AlertTriangle} label="Po splatnosti" value={fmt(summary.overdue_total)} sub={`${summary.overdue_count} faktur`} danger={summary.overdue_count > 0} testid="sum-overdue" />
                </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
                {FILTERS.map(([k, v]) => (
                    <button key={k} onClick={() => setFilter(k)} data-testid={`received-filter-${k || "all"}`}
                            className={`text-xs px-3 py-1.5 border uppercase tracking-wider ${filter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                        {v}
                    </button>
                ))}
            </div>

            <div className="bg-white border border-slate-200">
                {filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <FileInput className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádné přijaté faktury</div>
                        <div className="text-sm text-slate-500 mt-1">Vyfoťte fakturu mobilem nebo nahrajte PDF — AI ji vytěží za vás.</div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-5 py-3 font-semibold">Dodavatel</th>
                                <th className="px-5 py-3 font-semibold">Číslo / VS</th>
                                <th className="px-5 py-3 font-semibold">Zakázka</th>
                                <th className="px-5 py-3 font-semibold">Splatnost</th>
                                <th className="px-5 py-3 font-semibold text-right">Částka</th>
                                <th className="px-5 py-3 font-semibold">Stav</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((r, i) => (
                                <tr key={r.id} data-testid={`received-row-${r.id}`}
                                    className={`border-b border-slate-100 hover:bg-orange-50/40 ${r.due_state === "overdue" ? "bg-red-50/60" : r.due_state === "soon" ? "bg-amber-50/60" : i % 2 ? "bg-slate-50/40" : ""}`}>
                                    <td className="px-5 py-3">
                                        <button onClick={() => setEditing({ ...r, job_id: r.job_id || "" })} className="text-left font-medium text-slate-900 hover:text-orange-600">{r.supplier_name}</button>
                                        <div className="text-xs text-slate-500">{CATEGORIES[r.category] || r.category}{r.notes ? ` · ${r.notes}` : ""}</div>
                                    </td>
                                    <td className="px-5 py-3 text-xs">
                                        <div className="font-mono">{r.number || "—"}</div>
                                        {r.var_symbol && <div className="text-slate-500">VS {r.var_symbol}</div>}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-slate-600">{r.job_title || <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-amber-400 bg-amber-50 text-amber-800 rounded-sm" data-testid={`recv-no-job-${r.id}`}>bez zakázky</span>}</td>
                                    <td className="px-5 py-3">
                                        <div className={r.due_state === "overdue" ? "text-red-700 font-bold" : r.due_state === "soon" ? "text-amber-700 font-bold" : "text-slate-700"}>{fmtDate(r.due_date)}</div>
                                        {r.status === "neuhrazena" && r.days_to_due !== null && (
                                            <div className="text-[10px] uppercase" data-testid={`due-label-${r.id}`}>
                                                {r.days_to_due < 0 ? `${-r.days_to_due} dní po splatnosti!` : r.days_to_due === 0 ? "Splatné dnes!" : `za ${r.days_to_due} dní`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right font-semibold">{fmt(r.total)}</td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${r.status === "uhrazena" ? "bg-green-100 text-green-700 border-green-300" : r.status === "stornovana" ? "bg-slate-200 text-slate-600 border-slate-400" : "bg-blue-100 text-blue-700 border-blue-300"}`}>
                                            {r.status === "uhrazena" ? `Uhrazeno ${r.paid_at || ""}` : r.status === "stornovana" ? "Storno" : "Neuhrazeno"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        {r.file_url && <a href={fileUrl(r.file_url.replace("/api/files/", ""))} target="_blank" rel="noreferrer" className="text-xs text-orange-700 hover:underline mr-3" data-testid={`file-link-${r.id}`}>Doklad</a>}
                                        {r.status !== "stornovana" && (
                                            <button onClick={() => pay(r)} data-testid={`pay-toggle-${r.id}`}
                                                    className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 border rounded-sm ${r.status === "uhrazena" ? "border-slate-300 text-slate-500 hover:bg-slate-100" : "border-green-600 text-green-700 hover:bg-green-50 font-semibold"}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> {r.status === "uhrazena" ? "Vrátit" : "Zaplaceno"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <ReceivedInvoiceModal editing={editing} setEditing={setEditing} jobs={jobs} onSaved={load} onDelete={del} />
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value, sub, warn, danger, testid }) {
    return (
        <div className={`border-2 p-4 ${danger ? "border-red-400 bg-red-50" : warn ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
            <div className={`font-display text-2xl font-black tabular-data ${danger ? "text-red-700" : warn ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
            <div className="text-xs text-slate-500">{sub}</div>
        </div>
    );
}
